import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import OptimizedImage from '@/components/OptimizedImage';
import { API } from '@/App';
import axios from 'axios';
import { Calendar, User, ArrowRight } from 'lucide-react';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?published_only=true`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>Blog - Ener Yazılım | Teknoloji Yazıları ve Uzman Görüşleri</title>
        <meta name="description" content="Teknoloji dünyasındaki gelişmeler, yazılım ipuçları, siber güvenlik önerileri ve uzman görüşlerimiz." />
        <meta name="keywords" content="blog, teknoloji, yazılım, siber güvenlik, AI, web geliştirme, mobil uygulama" />
        <link rel="canonical" href="https://www.eneryazilim.com/blog" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="blog" />
        <meta property="og:url" content="https://www.eneryazilim.com/blog" />
        <meta property="og:title" content="Blog - Ener Yazılım" />
        <meta property="og:description" content="Teknoloji dünyasındaki gelişmeler ve uzman görüşlerimiz." />
        <meta property="og:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.eneryazilim.com/blog" />
        <meta name="twitter:title" content="Blog - Ener Yazılım" />
        <meta name="twitter:description" content="Teknoloji dünyasındaki gelişmeler ve uzman görüşlerimiz." />
        <meta name="twitter:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Structured Data - Blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Ener Yazılım Blog",
            "description": "Teknoloji dünyasındaki gelişmeler ve uzman görüşleri",
            "url": "https://www.eneryazilim.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Ener Yazılım",
              "url": "https://www.eneryazilim.com"
            },
            "blogPost": posts.slice(0, 10).map(post => ({
              "@type": "BlogPosting",
              "headline": post.title,
              "description": post.excerpt,
              "datePublished": post.created_at,
              "author": {
                "@type": "Person",
                "name": post.author
              },
              "url": `https://www.eneryazilim.com/blog/${post.slug}`
            }))
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden" data-testid="blog-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#F7931E]/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="blog-title">
              <span className="gradient-text">Blog</span>
            </h1>
            <p className="text-lg text-white/70">
              Teknoloji dünyasındaki gelişmeler ve uzman görüşlerimiz
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="pb-20" data-testid="blog-posts">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center text-white/60">Yükleniyor...</div>
          ) : posts.length === 0 ? (
            <div className="text-center text-white/60" data-testid="no-posts">Henüz blog yazısı eklenmemiş.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {posts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-${post.id}`}
                  className="glass rounded-2xl overflow-hidden card-hover block"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {post.image_url ? (
                    <OptimizedImage
                      src={post.image_url}
                      alt={post.title}
                      className="h-48"
                      loading="lazy"
                      objectFit="cover"
                    />
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20 flex items-center justify-center">
                      <div className="text-6xl opacity-50">📝</div>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-xs text-white/50 mb-3">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{post.title}</h3>
                    <p className="text-sm text-white/60 mb-4 line-clamp-3">{post.excerpt}</p>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="text-xs px-3 py-1 bg-white/5 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-[#FF6B35] text-sm font-medium">
                      <span>Devamını Oku</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;